import { useState } from 'react'
import {
  CubeIcon,
  UsersIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { useToast } from '../contexts/ToastContext'

const stats = [
  {
    name: 'Productos en inventario',
    stat: '124',
    icon: CubeIcon,
    change: '+2.1%',
    changeType: 'increase',
  },
  {
    name: 'Usuarios activos',
    stat: '12',
    icon: UsersIcon,
    change: '+5.4%',
    changeType: 'increase',
  },
  {
    name: 'Controles APPCC',
    stat: '8',
    icon: ShieldCheckIcon,
    change: '0%',
    changeType: 'neutral',
  },
  {
    name: 'Alertas pendientes',
    stat: '3',
    icon: ExclamationTriangleIcon,
    change: '-12.5%',
    changeType: 'decrease',
  },
]

const alerts = [
  {
    id: 1,
    type: 'warning',
    message: 'Stock bajo: Harina de trigo (5 kg restantes)',
    time: 'Hace 2 horas',
  },
  {
    id: 2,
    type: 'info',
    message: 'Control de temperatura programado para las 14:00',
    time: 'Hace 30 min',
  },
  {
    id: 3,
    type: 'error',
    message: 'Producto próximo a vencer: Leche entera (vence mañana)',
    time: 'Hace 1 hora',
  },
]

export default function Dashboard() {
  const toast = useToast()
  // TEMPORAL · SOLO DESARROLLO: reiniciar datos de prueba (quitar más adelante)
  const [resetting, setResetting] = useState(false)
  const resetTestData = async () => {
    if (!confirm('¿Borrar TODAS las órdenes de producción, compras, recepciones, inventario y expediciones?\n\nLos datos maestros (ingredientes, marcas, recetas…) NO se tocan. Esta acción no se puede deshacer.')) return
    setResetting(true)
    try {
      const res = await fetch('/api/v1/dev/dev-tools/reset-transactional', { method: 'POST', headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) toast.success(data.message || 'Datos de prueba reiniciados')
      else toast.error(data.message || 'No se pudo reiniciar')
    } catch {
      toast.error('Error de conexión')
    } finally {
      setResetting(false)
    }
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Resumen general del sistema de gestión de panadería
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((item) => (
          <div
            key={item.name}
            className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className="absolute bg-primary-500 rounded-md p-3">
                <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {item.name}
              </p>
            </dt>
            <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">
                {item.stat}
              </p>
              <p
                className={`ml-2 flex items-baseline text-sm font-semibold ${
                  item.changeType === 'increase'
                    ? 'text-green-600'
                    : item.changeType === 'decrease'
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
              >
                {item.change}
              </p>
            </dd>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Alertas recientes
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {alerts.map((alert) => (
              <div key={alert.id} className="px-6 py-4">
                <div className="flex items-start">
                  <div
                    className={`flex-shrink-0 w-2 h-2 mt-2 rounded-full ${
                      alert.type === 'error'
                        ? 'bg-red-400'
                        : alert.type === 'warning'
                        ? 'bg-yellow-400'
                        : 'bg-blue-400'
                    }`}
                  />
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-gray-900">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{alert.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Actividad reciente
            </h3>
          </div>
          <div className="p-6">
            <div className="flow-root">
              <ul className="-mb-8">
                <li className="relative pb-8">
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                        <CubeIcon className="h-5 w-5 text-white" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                      <div>
                        <p className="text-sm text-gray-500">
                          Nuevo producto agregado:{' '}
                          <span className="font-medium text-gray-900">
                            Pan integral
                          </span>
                        </p>
                      </div>
                      <div className="text-right text-sm whitespace-nowrap text-gray-500">
                        <time>Hace 1h</time>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="relative pb-8">
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                        <ShieldCheckIcon className="h-5 w-5 text-white" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                      <div>
                        <p className="text-sm text-gray-500">
                          Control APPCC completado:{' '}
                          <span className="font-medium text-gray-900">
                            Temperatura del horno
                          </span>
                        </p>
                      </div>
                      <div className="text-right text-sm whitespace-nowrap text-gray-500">
                        <time>Hace 2h</time>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="relative">
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center ring-8 ring-white">
                        <UsersIcon className="h-5 w-5 text-white" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                      <div>
                        <p className="text-sm text-gray-500">
                          Usuario creado:{' '}
                          <span className="font-medium text-gray-900">
                            María González
                          </span>
                        </p>
                      </div>
                      <div className="text-right text-sm whitespace-nowrap text-gray-500">
                        <time>Hace 3h</time>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* TEMPORAL · SOLO DESARROLLO — reiniciar datos de prueba. Quitar más adelante. */}
      <div className="mt-8 border border-dashed border-red-300 bg-red-50 rounded-lg p-4">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-red-800">Herramientas de desarrollo (temporal)</h3>
            <p className="text-sm text-red-700 mt-1">
              Vacía órdenes de producción, compras, recepciones, inventario y expediciones para empezar pruebas limpias.
              No afecta a ingredientes, marcas, recetas ni registros APPCC.
            </p>
          </div>
          <button
            onClick={resetTestData}
            disabled={resetting}
            className="mt-3 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            <TrashIcon className="h-5 w-5" />
            {resetting ? 'Reiniciando…' : 'Vaciar datos de prueba'}
          </button>
        </div>
      </div>
    </div>
  )
}